import { useMemo } from 'react';

// porp-types is a library for typechecking of props
import PropTypes from 'prop-types';

// @mui/x-charts components
import { PieChart as MuiPieChart } from '@mui/x-charts/PieChart';

// @mui material components
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

// Material Dashboard 2 PRO React base styles
import colors from 'assets/theme/base/colors';

function PieChart({ icon, title, description, height, chart }) {
    const series = [
        {
            data:
                chart.datasets?.data?.map((value, index) => ({
                    id: index,
                    value,
                    label: chart.labels?.[index] || `Item ${index}`,
                    color: chart.datasets?.backgroundColors?.[index]
                        ? colors.gradients[chart.datasets.backgroundColors[index]]?.state || colors.dark.main
                        : colors.dark.main,
                })) || [],
            innerRadius: 0,
            paddingAngle: 2,
            cornerRadius: 5,
            highlightScope: { faded: 'global', highlighted: 'item' },
        },
    ];

    const renderChart = (
        <MDBox py={2} pr={2} pl={icon.component ? 1 : 2}>
            {title || description ? (
                <MDBox display="flex" px={description ? 1 : 0} pt={description ? 1 : 0}>
                    {icon.component && (
                        <MDBox
                            width="4rem"
                            height="4rem"
                            bgColor={icon.color || 'info'}
                            variant="gradient"
                            coloredShadow={icon.color || 'info'}
                            borderRadius="xl"
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            color="white"
                            mt={-5}
                            mr={2}
                        >
                            <Icon fontSize="medium">{icon.component}</Icon>
                        </MDBox>
                    )}
                    <MDBox mt={icon.component ? -2 : 0}>
                        {title && <MDTypography variant="h6">{title}</MDTypography>}
                        <MDBox mb={2}>
                            <MDTypography component="div" variant="button" color="text">
                                {description}
                            </MDTypography>
                        </MDBox>
                    </MDBox>
                </MDBox>
            ) : null}
            {useMemo(
                () => (
                    <MDBox height={height}>
                        <MuiPieChart
                            series={series}
                            width="100%"
                            height={300}
                            slotProps={{
                                legend: { hidden: true },
                            }}
                        />
                    </MDBox>
                ),
                [chart, height]
            )}
        </MDBox>
    );

    return title || description ? <Card>{renderChart}</Card> : renderChart;
}

// Setting default values for the props of PieChart
PieChart.defaultProps = {
    icon: { color: 'info', component: '' },
    title: '',
    description: '',
    height: '19.125rem',
};

// Typechecking props for the PieChart
PieChart.propTypes = {
    icon: PropTypes.shape({
        color: PropTypes.oneOf(['primary', 'secondary', 'info', 'success', 'warning', 'error', 'light', 'dark']),
        component: PropTypes.node,
    }),
    title: PropTypes.string,
    description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    chart: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.array, PropTypes.object])).isRequired,
};

export default PieChart;
